import type { CommentInput, ReasonTag, SubmissionView } from "@/app/reviewQueries";
import { CellBox } from "@/routes/submission/CellBox";
import type { CellRow } from "@/routes/submission/model";
import { ThreadView } from "@/routes/submission/ThreadView";
import { Composer } from "./Composer";

const MARK: Record<string, { mark: string; fg: string; meta: (r: CellRow) => string }> = {
  failed: { mark: "✕", fg: "var(--err)", meta: (r) => r.kind || "упало" },
  passed: { mark: "✓", fg: "var(--ok)", meta: () => "проверки прошли" },
  idle: { mark: "—", fg: "var(--mut)", meta: () => "не запускалось" },
  none: { mark: "·", fg: "var(--mut)", meta: () => "" },
};

const STRIP: Record<string, { bg: string; bd: string }> = {
  failed: { bg: "var(--err)", bd: "var(--err)" },
  passed: { bg: "var(--acc)", bd: "var(--acc)" },
  idle: { bg: "transparent", bd: "var(--bd2)" },
  none: { bg: "transparent", bd: "var(--bd)" },
};

export const cellAnchor = (row: CellRow) => `cell-${row.n || row.cellId}`;

/** Левая колонка: мини-полоска и список задач; клик прокручивает к ячейке. */
export function CellNav({ rows }: { rows: CellRow[] }) {
  const jump = (row: CellRow) => document.getElementById(cellAnchor(row))?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <div className="flex flex-col gap-3" data-testid="cell-nav">
      <div className="font-mono text-[11px] uppercase tracking-[.06em] text-mut">ячейки</div>
      <div className="flex gap-[2px]" aria-hidden>
        {rows.map((r) => (
          <span key={r.cellId} className="h-[7px] flex-1 rounded-[1px]" style={{ background: STRIP[r.status]!.bg, border: `1px solid ${STRIP[r.status]!.bd}` }} />
        ))}
      </div>
      <div className="flex flex-col gap-px">
        {rows.map((r) => {
          const m = MARK[r.status]!;
          return (
            <button
              key={r.cellId}
              type="button"
              onClick={() => jump(r)}
              className="grid cursor-pointer grid-cols-[18px_minmax(0,1fr)_16px] items-center gap-1.5 rounded-sm border border-transparent bg-transparent px-1.5 py-[5px] text-left hover:bg-card"
              style={{ color: "var(--ink2)" }}
            >
              <span className="font-mono text-[11px]" style={{ color: m.fg }}>
                {m.mark}
              </span>
              <span className="min-w-0">
                <span className="block truncate font-mono text-[12px] text-ink">{r.n ? `Задача ${r.n}` : `ячейка ${r.cellId}`}</span>
                {m.meta(r) && <span className="block truncate font-mono text-[10.5px] text-ink2">{m.meta(r)}</span>}
              </span>
              {r.threads.length > 0 && <span aria-label="есть комментарий" className="size-[7px] rounded-full" style={{ background: "var(--err)" }} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Ячейки версии с тредами и композером (один открыт за раз). */
export function Cells({
  rows,
  view,
  composing,
  drafts,
  reasons,
  me,
  canComment,
  onCompose,
  onDraft,
  onCancel,
  onSubmit,
  onReply,
}: {
  rows: CellRow[];
  view: SubmissionView;
  composing: string | null;
  drafts: Record<string, string>;
  reasons: ReasonTag[];
  me: string;
  canComment: boolean;
  onCompose: (cellId: string) => void;
  onDraft: (cellId: string, text: string) => void;
  onCancel: () => void;
  onSubmit: (input: CommentInput) => Promise<void>;
  onReply: (parentId: string, cellId: string, taskN: number | undefined, body: string) => Promise<void>;
}) {
  return (
    <div className="flex flex-col gap-3">
      {rows.map((row) => {
        const isComposing = composing === row.cellId;
        return (
          <CellBox
            key={row.cellId}
            id={cellAnchor(row)}
            row={row}
            right={
              <>
                <span className="font-mono text-[11px] text-mut">код версии v{view.iteration} — в следующем подпроекте</span>
                {canComment && (
                  <button
                    type="button"
                    onClick={() => onCompose(row.cellId)}
                    disabled={isComposing}
                    className="rounded-[3px] border border-bd2 bg-transparent px-2 py-[3px] font-mono text-[11px] hover:border-acc disabled:cursor-default"
                    style={{ color: "var(--ink2)", cursor: isComposing ? "default" : "pointer" }}
                  >
                    {isComposing ? "пишешь…" : "+ комментарий"}
                  </button>
                )}
              </>
            }
          >
            {row.threads.map((t) => (
              <ThreadView key={t.root.id} thread={t} view={view} reply={{ me: { name: me, student: false }, onSend: (body) => onReply(t.root.id, t.root.cell_id, t.root.task_n, body) }} />
            ))}
            {isComposing && (
              <Composer cellId={row.cellId} taskN={row.n || undefined} draft={drafts[row.cellId] ?? ""} onDraft={(t) => onDraft(row.cellId, t)} reasons={reasons} me={me} onSubmit={onSubmit} onCancel={onCancel} />
            )}
          </CellBox>
        );
      })}
    </div>
  );
}
